const API_BASE_URL = process.env.REACT_APP_API_URL;

// Helper function to detect if a file is a video
const isVideoFile = (fileUrl, mimeType = null) => {
  if (!fileUrl) return false;
  
  // Check MIME type first if available
  if (mimeType && mimeType.startsWith('video/')) {
    return true;
  }
  
  // Check file extension
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.m4v', '.3gp'];
  const urlLower = fileUrl.toLowerCase();
  return videoExtensions.some(ext => urlLower.includes(ext));
};

export const getHomeData = async () => {
  try {
    let response;
    let data;
    
    // Strategy 1: Current approach
    try {
      response = await fetch(`${API_BASE_URL}/home?populate[carosalImages][populate]=*`);
      if (response.ok) {
        data = await response.json();
      }
    } catch (error) {
      console.warn('⚠️ Strategy 1 failed:', error.message);
    }
    
    // Strategy 2: If strategy 1 failed or returned no carousel images, try deeper populate
    if (!data || !data.data?.carosalImages) {
      try {
        response = await fetch(`${API_BASE_URL}/home?populate=deep`);
        if (response.ok) {
          data = await response.json();
          console.log('✅ Strategy 2 successful');
        }
      } catch (error) {
        console.warn('⚠️ Strategy 2 failed:', error.message);
      }
    }
    
    // Strategy 3: Try with different field names
    if (!data || !data.data?.carosalImages) {
      try {
        response = await fetch(`${API_BASE_URL}/home?populate[carousel][populate]=*`);
        if (response.ok) {
          data = await response.json();
          console.log('✅ Strategy 3 successful');
        }
      } catch (error) {
        console.warn('⚠️ Strategy 3 failed:', error.message);
      }
    }
    
    if (!response || !response.ok) {
      throw new Error(`HTTP error! status: ${response?.status || 'Unknown'}`);
    }

    return formatHomeData(data.data);
  } catch (error) {
    console.error('❌ Error fetching home data:', error);
    throw error;
  }
};

/**
 * Format home data from Strapi API
 * 
 * Schema structure:
 * - carosalImages: Single gallery-images component (not repeatable)
 *   - images: Array of image objects within the gallery component
 * - homeHeaderDesc: Single mission-vision component
 * - clientsImages: Single gallery-images component
 */
const formatHomeData = (homeData) => {
  if (!homeData) {
    return null;
  }
  // Format header description
  const headerDesc = homeData.homeHeaderDesc ? {
    title: homeData.homeHeaderDesc.title,
    description: homeData.homeHeaderDesc.description
  } : null;

  // Format carousel images - Updated to handle multiple possible API structures
  const carouselImages = (() => {
    // Try different possible field names and structures
    const carouselSources = [
      homeData.carosalImages,  // Original field name
      homeData.carousel,       // Alternative field name
      homeData.carouselImages, // Standard naming
      homeData.slider,         // Another common name
      homeData.hero_images,    // Hero images
      homeData.banner_images   // Banner images
    ];
   
    
    for (const [index, carouselSource] of carouselSources.entries()) {
      if (!carouselSource) continue;
      
      
      // Case 1: Direct media relation (carosalImages.data array)
      if (carouselSource.data && Array.isArray(carouselSource.data)) {
        const images = carouselSource.data.map((item, imageIndex) => {
          if (item?.attributes?.url || item?.url) {
            const imageData = item.attributes || item;
            const fullUrl = imageData.url.startsWith('http') ? imageData.url : `${API_BASE_URL.replace('/api', '')}${imageData.url}`;
            const itemIsVideo = isVideoFile(imageData.url, imageData.mime);
            
            return {
              id: `carousel-media-${imageIndex}`,
              url: fullUrl,
              alt: imageData?.alternativeText || imageData?.name || `Carousel ${itemIsVideo ? 'video' : 'image'} ${imageIndex + 1}`,
              formats: imageData?.formats || null,
              thumbnail: imageData?.formats?.thumbnail?.url ? 
                (imageData.formats.thumbnail.url.startsWith('http') ? 
                  imageData.formats.thumbnail.url : 
                  `${API_BASE_URL.replace('/api', '')}${imageData.formats.thumbnail.url}`) : null,
              isVideo: itemIsVideo,
              videoUrl: itemIsVideo ? fullUrl : null,
              mimeType: imageData?.mime || null
            };
          }
          return null;
        }).filter(Boolean);
      }
      
      // Case 2: Array of gallery components
      if (Array.isArray(carouselSource)) {
        const images = [];
        
        carouselSource.forEach((carouselComponent, componentIndex) => {
          if (carouselComponent?.images && Array.isArray(carouselComponent.images)) {
            carouselComponent.images.forEach((item, imageIndex) => {
              if (item?.url) {
                const fullUrl = item.url.startsWith('http') ? item.url : `${API_BASE_URL.replace('/api', '')}${item.url}`;
                const itemIsVideo = isVideoFile(item.url, item.mime);
          
                images.push({
                  id: `carousel-${componentIndex}-${imageIndex}`,
                  url: fullUrl,
                  alt: item?.alternativeText || item?.name || `Carousel ${itemIsVideo ? 'video' : 'image'} ${images.length + 1}`,
                  formats: item?.formats || null,
                  thumbnail: item?.formats?.thumbnail?.url ? 
                    (item.formats.thumbnail.url.startsWith('http') ? 
                      item.formats.thumbnail.url : 
                      `${API_BASE_URL.replace('/api', '')}${item.formats.thumbnail.url}`) : null,
                  isVideo: itemIsVideo,
                  videoUrl: itemIsVideo ? fullUrl : null,
                  mimeType: item?.mime || null
                });
              }
            });
          }
        });
        
        if (images.length > 0) {
          console.log('✅ Found carousel images in gallery array:', images.length);
          return images;
        }
      }
      
      // Case 3: Single gallery component with images array
      if (carouselSource?.images && Array.isArray(carouselSource.images)) {
        console.log('📷 SINGLE GALLERY COMPONENT detected in source', index);
        const images = carouselSource.images.map((item, imageIndex) => {
          if (item?.url) {
            const fullUrl = item.url.startsWith('http') ? item.url : `${API_BASE_URL.replace('/api', '')}${item.url}`;
            const itemIsVideo = isVideoFile(item.url, item.mime);
            
            return {
              id: `carousel-single-${imageIndex}`,
              url: fullUrl,
              alt: item?.alternativeText || item?.name || `Carousel ${itemIsVideo ? 'video' : 'image'} ${imageIndex + 1}`,
              formats: item?.formats || null,
              thumbnail: item?.formats?.thumbnail?.url ? 
                (item.formats.thumbnail.url.startsWith('http') ? 
                  item.formats.thumbnail.url : 
                  `${API_BASE_URL.replace('/api', '')}${item.formats.thumbnail.url}`) : null,
              isVideo: itemIsVideo,
              videoUrl: itemIsVideo ? fullUrl : null,
              mimeType: item?.mime || null
            };
          }
          return null;
        }).filter(Boolean);
        
        if (images.length > 0) {
          return images;
        }
      }
    }
    
    return [];
  })();

// Format client images - For now return empty since clientsImages is not in API response
const clientSection = (() => {
  return null;
})();

// Format statistics with colors
const statisticColors = ["#E05252", "#03A893", "#D49D02", "#3757F7"];
const statistics = homeData.homeStates?.map((stat, index) => ({
  id: stat.id,
  number: stat.number?.trim() || '',
  label: stat.description?.trim() || '',
  color: statisticColors[index % statisticColors.length]
})) || [];

// Format floating statistics
const floatingStats = homeData.floatingStats?.map(stat => ({
  id: stat.id,
  number: stat.number?.trim() || '',
  label: stat.description?.trim() || ''
})) || [];

return {
  id: homeData.id,
  headerDesc,
  carouselImages,
  clientSection,
  statistics,
  floatingStats,
  rawData: homeData
};
};

const homeService = {
  getHomeData
};

export default homeService;
